import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { BizCard, type BizItem } from "@/components/BizCard";
import { useRegion } from "@/contexts/RegionContext";
import { useLocation as useLocationContext } from "@/contexts/LocationContext";
import { calculateDistance } from "@/lib/geolocation";
import { regions, australianCities, businessCategories } from "@shared/schema";
import { Building2, MapPin, ShieldCheck, Star } from "lucide-react";

export default function BizPromo() {
  const { user } = useAuth();
  const { selectedRegion, selectedSuburb } = useRegion();
  const { userLocation } = useLocationContext();
  const [selectedState, setSelectedState] = useState<string>(selectedRegion || "ALL");
  const [selectedCity, setSelectedCity] = useState<string>("ALL");
  const [searchTerm, setSearchTerm] = useState<string>("");
  // Phase 3.6: 비즈홍보 전용 필터
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [onlyVerifiedABN, setOnlyVerifiedABN] = useState<boolean>(false);

  // Phase 3.6: 비즈홍보 목록 조회 (business-directory API 사용)
  const { data: listings, isLoading } = useQuery<BizItem[]>({
    queryKey: ["/api/business-directory", selectedState, selectedCity],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append("status", "active");
      if (selectedState !== "ALL") params.append("region", selectedState);
      if (selectedCity !== "ALL") params.append("city", selectedCity);

      const res = await fetch(`/api/business-directory?${params.toString()}`);
      if (!res.ok) throw new Error("Failed to fetch listings");
      const data = await res.json();
      // 응답이 배열일 수 있음
      if (Array.isArray(data)) {
        return data;
      }
      return [];
    },
  });

  // Phase 3.6: 거리 계산 및 고급 필터링 로직
  const filteredListings = useMemo(() => {
    if (!listings) return [];

    // 거리 계산
    let result = listings.map((listing) => {
      let distance: number | undefined = undefined;
      if (
        userLocation &&
        listing.latitude &&
        listing.longitude &&
        typeof listing.latitude === "number" &&
        typeof listing.longitude === "number"
      ) {
        distance = calculateDistance(
          userLocation.latitude,
          userLocation.longitude,
          listing.latitude,
          listing.longitude
        );
      }
      return { ...listing, distance };
    });

    // 필터링
    result = result.filter((listing) => {
      // 검색어 필터
      if (
        searchTerm &&
        !listing.businessName?.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !listing.businessNameKo?.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !listing.description?.toLowerCase().includes(searchTerm.toLowerCase())
      ) {
        return false;
      }

      // 카테고리 필터
      if (selectedCategory !== "all" && listing.category !== selectedCategory) {
        return false;
      }

      // Phase 3.6: ABN 인증 필터 (ABN 필수)
      if (onlyVerifiedABN && !listing.verifiedABN) {
        return false;
      }

      // ABN이 없는 비즈니스는 제외 (ABN 필수)
      if (!listing.abn && !listing.verifiedABN) {
        return false;
      }

      return true;
    });

    // 프리미엄/플래티넘 우선 정렬, 그 다음 거리순
    return result.sort((a, b) => {
      // 프리미엄/플래티넘 우선
      const aTier = a.membershipTier === "platinum" ? 3 : a.membershipTier === "premium" ? 2 : 1;
      const bTier = b.membershipTier === "platinum" ? 3 : b.membershipTier === "premium" ? 2 : 1;
      if (aTier !== bTier) return bTier - aTier;

      // 거리순 정렬 (거리가 있는 경우)
      if (a.distance !== undefined && b.distance !== undefined) {
        return a.distance - b.distance;
      }
      if (a.distance !== undefined) return -1;
      if (b.distance !== undefined) return 1;

      // 최신순 정렬
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
  }, [listings, searchTerm, selectedCategory, onlyVerifiedABN, userLocation]);

  const stateRegion = regions.find((r) => r.id === selectedState);
  const availableCities =
    selectedState !== "ALL" && selectedState in australianCities
      ? australianCities[selectedState as keyof typeof australianCities]
      : [];

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <div className="bg-primary/5 border-b">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl font-bold mb-3" data-testid="text-title">
              비즈홍보
            </h1>
            <p className="text-lg text-muted-foreground mb-6">
              ABN 인증 비즈니스 홍보 - 식당, 클리닉, 미용, 수리, 병원 등
            </p>
            <div className="flex gap-4 justify-center items-center flex-wrap">
              <Badge variant="secondary" className="gap-1">
                <Building2 className="w-3 h-3" />
                ABN 필수
              </Badge>
              <Badge variant="secondary" className="gap-1">
                <MapPin className="w-3 h-3" />
                지역기반
              </Badge>
              <Badge variant="secondary" className="gap-1">
                <ShieldCheck className="w-3 h-3" />
                인증 비즈니스
              </Badge>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Sidebar - Filters */}
          <aside className="lg:col-span-1">
            <Card>
              <CardContent className="p-4 space-y-4">
                {/* Search */}
                <div>
                  <label className="text-sm font-medium mb-2 block">검색</label>
                  <Input
                    placeholder="상호명 또는 설명 검색..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    data-testid="input-search"
                  />
                </div>

                {/* State Filter */}
                <div>
                  <label className="text-sm font-medium mb-2 block">
                    주/지역
                  </label>
                  <Select
                    value={selectedState}
                    onValueChange={(value) => {
                      setSelectedState(value);
                      setSelectedCity("ALL");
                    }}
                  >
                    <SelectTrigger data-testid="select-state">
                      <SelectValue placeholder="주 선택" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ALL">전체 지역</SelectItem>
                      {regions
                        .filter((r) => r.id !== "ALL")
                        .map((region) => (
                          <SelectItem key={region.id} value={region.id}>
                            {region.label}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* City Filter */}
                {availableCities.length > 0 && (
                  <div>
                    <label className="text-sm font-medium mb-2 block">
                      도시
                    </label>
                    <Select
                      value={selectedCity}
                      onValueChange={setSelectedCity}
                    >
                      <SelectTrigger data-testid="select-city">
                        <SelectValue placeholder="도시 선택" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ALL">전체 도시</SelectItem>
                        {availableCities.map((city) => (
                          <SelectItem key={city.id} value={city.id}>
                            {city.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Phase 3.6: 업종 필터 */}
                <div>
                  <label className="text-sm font-medium mb-2 block">
                    업종
                  </label>
                  <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">전체</SelectItem>
                      {businessCategories.map((cat) => (
                        <SelectItem key={cat.id} value={cat.id}>
                          {cat.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Phase 3.6: ABN 인증만 보기 */}
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="only-verified-abn"
                    checked={onlyVerifiedABN}
                    onChange={(e) => setOnlyVerifiedABN(e.target.checked)}
                    className="h-4 w-4"
                  />
                  <label
                    htmlFor="only-verified-abn"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    ABN 인증만 보기
                  </label>
                </div>

                {/* Post Button */}
                {user && (
                  <Link href="/biz/register">
                    <Button className="w-full" data-testid="button-register">
                      비즈니스 등록
                    </Button>
                  </Link>
                )}
                {!user && (
                  <Button className="w-full" variant="outline" disabled>
                    로그인 후 등록 가능
                  </Button>
                )}
              </CardContent>
            </Card>
          </aside>

          {/* Main Content - Listings */}
          <div className="lg:col-span-3">
            {/* Results Count */}
            <div className="mb-4 flex justify-between items-center">
              <p
                className="text-sm text-muted-foreground"
                data-testid="text-count"
              >
                {filteredListings
                  ? `${filteredListings.length}개의 비즈니스`
                  : "로딩 중..."}
              </p>
            </div>

            {/* Listings Grid */}
            {isLoading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {[...Array(6)].map((_, i) => (
                  <Card key={i}>
                    <Skeleton className="h-48 w-full" />
                    <CardContent className="p-4">
                      <Skeleton className="h-4 w-3/4 mb-2" />
                      <Skeleton className="h-4 w-1/2" />
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : filteredListings && filteredListings.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {filteredListings.map((listing) => (
                  <BizCard
                    key={listing.id}
                    item={listing}
                    onInquire={() => {
                      // TODO: 채팅 시작 로직
                      if (listing.kakaoChannel) {
                        window.open(listing.kakaoChannel, "_blank");
                      } else {
                        window.location.href = `/chat-start/biz/${listing.id}`;
                      }
                    }}
                  />
                ))}
              </div>
            ) : (
              <Card>
                <CardContent className="py-16 text-center">
                  <Building2 className="w-16 h-16 mx-auto mb-4 text-muted-foreground/30" />
                  <p className="text-lg font-medium mb-2">
                    등록된 비즈니스가 없습니다
                  </p>
                  <p className="text-sm text-muted-foreground mb-6">
                    첫 번째 비즈니스를 등록해보세요! (ABN 필수)
                  </p>
                  {user && (
                    <Link href="/biz/register">
                      <Button>비즈니스 등록하기</Button>
                    </Link>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

